import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization: '',
    message: '',
    gdpr_consent: false
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdpr_consent) {
      toast.error('Prašome sutikti su duomenų tvarkymo taisyklėmis');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      toast.success('Jūsų žinutė sėkmingai išsiųsta!');
      setFormData({
        full_name: '',
        email: '',
        organization: '',
        message: '',
        gdpr_consent: false
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Klaida siųsti žinutę. Bandykite dar kartą.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MessageCircle className="w-4 h-4" />
              <span>Susisiekite</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
              Pradėkime <span className="text-[#4CAF50]">pokalbį</span>
            </h1>
            <p className="text-lg text-[#718096]">
              Turite klausimų arba norite užsisakyti mokymus? Parašykite man!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-lg">
              <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-6">
                Parašykite žinutę
              </h2>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-2">
                    Vardas ir Pavardė *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    data-testid="fullname-input"
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition-colors"
                    placeholder="Jonas Jonaitis"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-2">
                    El. paštas *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition-colors"
                    placeholder="jusu@pastas.lt"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-2">
                    Organizacija (neprivaloma)
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    data-testid="organization-input"
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition-colors"
                    placeholder="Jūsų įmonės pavadinimas"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-2">
                    Žinutė *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="message-textarea"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition-colors resize-none"
                    placeholder="Parašykite savo žinutę..."
                  />
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onChange={handleChange}
                    required
                    data-testid="gdpr-checkbox"
                    className="w-5 h-5 mt-0.5 rounded border-slate-300 text-[#4CAF50] focus:ring-[#4CAF50]"
                  />
                  <label className="ml-3 text-sm text-[#718096]">
                    Sutinku, kad mano duomenys būtų tvarkomi pagal{' '}
                    <a href="/privatumas" className="text-[#4CAF50] hover:underline">
                      privatumo politiką
                    </a>
                    .
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-button"
                  className="w-full bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full py-4 font-semibold transition-all hover:-translate-y-1 shadow-lg shadow-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? 'Siųčiama...' : (
                    <>
                      Siųsti žinutę
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-[#FAFAF8] rounded-3xl p-8">
                <h3 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-6">
                  Kontaktai
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#4CAF50]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2D3748] mb-1">El. paštas</p>
                      <a href="mailto:info@tavoemocijos.lt" className="text-[#718096] hover:text-[#4CAF50] transition-colors">
                        info@tavoemocijos.lt
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#FFF3E0] flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#FF9F43]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2D3748] mb-1">Telefonas</p>
                      <a href="tel:+37067838321" className="text-[#718096] hover:text-[#4CAF50] transition-colors">
                        +370 678 38321
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#5B9BD5]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2D3748] mb-1">Adresas</p>
                      <p className="text-[#718096]">
                        Vilnius, Lietuva
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#E8F5E9] rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Clock className="w-6 h-6 text-[#4CAF50]" />
                  <h3 className="text-xl font-['Poppins'] font-semibold text-[#2D3748]">
                    Darbo laikas
                  </h3>
                </div>
                <div className="space-y-2 text-[#718096]">
                  <p>Pirmadienis - Penktadienis: 9:00 - 18:00</p>
                  <p>Šeštadienis: 10:00 - 14:00</p>
                  <p>Sekmadienis: Nedirbame</p>
                </div>
              </div>

              <div className="bg-[#F3E5F5] rounded-3xl p-8">
                <h3 className="text-xl font-['Poppins'] font-semibold text-[#2D3748] mb-3">
                  Greitas atsakymas
                </h3>
                <p className="text-[#718096]">
                  Paprastai atsakau per 24 valandas. Jei reikalas skubus, skambinkite telefonu.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
